/**
 * Central place for tunable game values.
 * Keep gameplay constants here so balancing the game never requires
 * touching rendering or rules code.
 *
 * Per-level values (grid size, speed, target length, obstacles, ...)
 * live in level definitions (see `src/levels`), not here - this file
 * only holds values that apply across the whole app.
 */
export const GameConfig = {
  /**
   * Cross-campaign gameplay constants that aren't tied to any one level's
   * shape/difficulty.
   */
  gameplay: {
    /**
     * FROZEN LEGACY VALUE - PR11 (Speed Engine V2). Campaign levels no
     * longer read this; see `worldBaseStepMs` below for the current
     * per-world starting speed. Kept, unchanged, only because Daily
     * Generator V1 and V2 (`src/daily/generator.ts`'s frozen
     * `buildCandidateV1`/`buildCandidateV2`/their fallback builders) still
     * reference it for byte-for-byte-compatible legacy output - changing
     * this number would silently change what an already-generated V1/V2
     * Daily spec means. Never reference this for anything new.
     */
    baseStepMs: 150,
    /**
     * PR11 (Speed Engine V2): one starting `baseStepMs` per Campaign
     * world, keyed by `WorldDefinition.id` - every level in a world starts
     * at its world's value, never a level-local override (see
     * `world1.ts`/`world2.ts`). Slower per world than the old shared
     * 150ms default, matching the audited candidate model. All five
     * worlds (Moon/Earth/Mars/Saturn/Sun) are complete, 25 levels total -
     * see `WorldRegistry.ts`.
     */
    worldBaseStepMs: {
      w1: 170, // THE MOON
      w2: 165, // EARTH
      w3: 160, // MARS
      w4: 155, // SATURN
      w5: 150, // SUN
    } as Record<string, number>,
  },

  /**
   * PR11 (Speed Engine V2): the single authoritative set of tunables for
   * `src/core/SpeedEngine.ts`'s pure composition formula - see that
   * module's own doc for the exact math. Kept here (not hardcoded in the
   * engine) for the same reason every other balance constant lives in
   * `GameConfig`: tuning never requires touching engine code.
   */
  speedEngine: {
    /**
     * Absolute minimum movement-step intervals, by mode - a floor on the
     * FINAL (composed, or fixed-overridden) step interval, never a floor
     * on any individual boost term. `temporaryMs` applies only while an
     * authorized temporary effect (SPEED TRIAL) is active; `normalMs`
     * applies otherwise. `temporaryMs` is deliberately equal to
     * `speedTrialFixedStepMs` below in both modes - SPEED TRIAL's fixed
     * target sits exactly at the temporary floor by design, not merely by
     * coincidence of hitting it.
     */
    /**
     * PR12 (growth/normal-cap tuning): `normalMs` raised (lower interval,
     * higher max speed) in both modes - 100ms -> 95ms Campaign, 90ms ->
     * 85ms Daily. `temporaryMs`/SPEED TRIAL's fixed target are untouched.
     */
    campaignCaps: { normalMs: 95, temporaryMs: 75 },
    dailyCaps: { normalMs: 85, temporaryMs: 75 },
    /**
     * PR25 (Daily V6): its own, LOWER normal-play floor than V1-V5's
     * `dailyCaps` - the PR's explicit "75ms is the absolute V6
     * normal-play floor" requirement (Redline speed profile + Relentless
     * pressure must be able to sustain exactly 75ms). Kept as its own
     * named cap, never a change to `dailyCaps` itself, so an already-
     * stored V1-V5 Daily row's speed behavior is completely unaffected -
     * see `LevelDefinition.speedMode`'s `"daily-v6"` doc.
     */
    dailyV6Caps: { normalMs: 75, temporaryMs: 75 },
    /**
     * Endless V1: a PERMANENT cap, unlike Campaign/Daily's higher normal
     * cap - Endless has no target length to reach and stop at, so its
     * fastest sustained speed IS the game's fastest sustained speed. Numerically
     * equal to `speedTrialFixedStepMs` below (both 75ms), but deliberately
     * its own named constant, never a reference to that field - the two
     * are allowed to be tuned independently in the future, and this way
     * neither change can silently affect the other.
     */
    endlessCaps: { normalMs: 75, temporaryMs: 75 },
    /**
     * PR11.1 (SPEED TRIAL fixed-interval correction): the exact, fixed
     * movement interval SPEED TRIAL forces while active, in both Campaign
     * and Daily - a deliberate, named, configured value, never a
     * percentage boost and never derived from whatever speed the player
     * had at trial entry. See `SpeedEngine`'s `SPEED_TRIAL_FIXED_STEP_MS`/
     * `resolveEffectiveTargetStepMs()`.
     */
    speedTrialFixedStepMs: 75,
    /**
     * Growth-derived speed boost: a percentage-point bonus per segment the
     * snake has actually grown past its starting length this run, in
     * three progressively smaller tiers (1st-10th added segment worth
     * more than the 11th-20th, worth more than the 21st+). Real length
     * only - a bite whose growth hasn't been applied to the body yet
     * (mid-tick) never counts.
     *
     * PR12 (growth/normal-cap tuning): tiers raised from 1.5%/1.0%/0.5%
     * to 2%/1.25%/0.75% per segment so ordinary SAFE/red growth
     * accelerates the snake more noticeably during a typical level.
     */
    growthBoost: {
      tier1MaxAdded: 10,
      tier1PerSegment: 0.02,
      tier2MaxAdded: 20,
      tier2PerSegment: 0.0125,
      tier3PerSegment: 0.0075,
    },
    /**
     * Permanent RUSH debt: a percentage-point bonus per cumulative yellow
     * (RUSH) fruit eaten this run, in three progressively smaller tiers.
     * Cumulative and never reset by SAFE/CHAOS/STARFRUIT/gate-opening -
     * only a brand new run zeroes it. Deliberately separate from the RUSH
     * scoring combo (`GameConfig.bites.rush.scoreByStack`/`maxStack`),
     * which resets independently - see `LevelGame`'s combo-vs-debt split.
     *
     * PR13-A (RUSH debt rebalance): tiers raised from 5%/4%/3% to
     * 7%/5%/3% per fruit, so an early RUSH pick carries a materially
     * larger permanent speed cost relative to SAFE - see
     * `rushDebtLegacyV1` below for the frozen pre-PR13-A values, which
     * Daily Generator V1-V4 continue to use forever (see
     * `LevelDefinition.rushDebtProfile`/`SpeedEngine.computeRushDebtBoost`).
     * Cumulative debt by RUSH count: 0=0%, 1=7%, 2=14%, 3=21%, 4=26%,
     * 5=31%, 6=36%, 7=39%, 8=42%, 9=45%, 10=48%.
     */
    rushDebt: {
      tier1Count: 3,
      tier1PerFruit: 0.07,
      tier2Count: 3,
      tier2PerFruit: 0.05,
      tier3PerFruit: 0.03,
    },
    /**
     * FROZEN pre-PR13-A RUSH debt tiers (5%/4%/3%) - Campaign and Daily
     * Generator V5 use `rushDebt` above instead; Daily Generator V1-V4
     * (`generatorVersion` 1-4, `speedEngineVersion` undefined/2/3, never
     * `SPEED_ENGINE_VERSION_V5`) use this frozen table exactly forever, so
     * an already-generated/stored legacy Daily challenge's speed behavior
     * never silently changes. See `LevelDefinition.rushDebtProfile`.
     */
    rushDebtLegacyV1: {
      tier1Count: 3,
      tier1PerFruit: 0.05,
      tier2Count: 3,
      tier2PerFruit: 0.04,
      tier3PerFruit: 0.03,
    },
    /** Daily's `high_speed` modifier (V3+ only - see `src/daily/generator.ts`): a flat, persistent percentage-point bonus present from the first movement step of the run, on top of everything else. Every other Daily modifier/Campaign run uses 0. */
    highSpeedModifierBoost: 0.08,
    /** Smooth transition durations, in ms of active time - see `SpeedEngine`'s transition class. Deliberately independent of SPEED TRIAL's own 6000ms gameplay duration (`GameConfig.bites.chaos.speedTrial.durationMs`). */
    transitionMs: {
      ordinary: 400,
      speedTrialEntry: 200,
      speedTrialExit: 400,
    },
  },

  input: {
    /** Max buffered direction changes, so fast key/swipe input isn't dropped. */
    queueMax: 2,
    /** Minimum swipe distance in pixels before it counts as a swipe. */
    swipeMinDistance: 24,
  },

  storage: {
    progressKey: "snake-arena-progress-v1",
    tutorialSeenKey: "snake-arena-tutorial-seen-v1",
    /** Non-authoritative Daily Challenge display cache only - see `src/daily/DailyLocalState.ts`. Never used to decide attempts remaining; the backend (`daily_participation`) is always the source of truth. Entirely separate from `progressKey` - clearing this can never affect campaign progress, stars, or best scores, and vice versa. */
    dailyUiKey: "snake-arena-daily-ui-v1",
    /** QA-only Daily Challenge overrides (date/simulated attempts) - see `src/daily/dailyQa.ts`. Read only on an allowed host (local dev / this project's Deploy Preview pattern); never read or honored on production. */
    dailyQaKey: "snake-arena-daily-qa-v1",
    /** The player's Sound On/Off choice - see `src/audio/AudioPreferences.ts`. Entirely separate from every other storage key here; never touched by progress/Daily/Score reads or writes, and vice versa. */
    audioPreferencesKey: "snake-arena-audio-v1",
  },

  /**
   * "CHOOSE YOUR BITE": every food round spawns three fruits at once -
   * SAFE, RUSH and CHAOS - and the player picks exactly one.
   */
  bites: {
    safe: {
      growth: 1,
      score: 15,
    },
    rush: {
      growth: 2,
      /**
       * Score awarded for a RUSH streak of N (1-indexed) - not a flat
       * multiplier: the marginal value per extra RUSH tapers off on
       * purpose, so a long streak's *risk* (speed) outpaces its *reward*
       * (score). Index `maxStack - 1` is reused for every streak beyond
       * the cap.
       *
       * PR26 (RUSH Score V2): lowered from [20, 35, 50, 65, 80] - the
       * Score reward now plateaus at the 5th consecutive RUSH while the
       * SPEED risk (`GameConfig.speedEngine.rushDebt`, and PR26's new
       * post-95 Campaign continuation below) keeps increasing every
       * further RUSH pick, deliberately decoupling "reward stops
       * escalating" from "risk stops escalating" - see CHANGE 3/4 of the
       * PR26 implementation.
       */
      scoreByStack: [20, 30, 40, 50, 60],
      /**
       * RUSH streak multiplier caps at x5 - display/score only, tracked by
       * its own combo counter, entirely separate from permanent RUSH
       * debt (`GameConfig.speedEngine.rushDebt`, driven by the run's
       * cumulative yellow count, never reset by SAFE/CHAOS/STARFRUIT). See
       * `LevelGame`'s combo-vs-debt split (PR11 / Speed Engine V2).
       */
      maxStack: 5,
    },
    chaos: {
      /**
       * PR13-A (versioned CHAOS V2 outcome pool): the CURRENT, active
       * outcome pool - Campaign and Daily Generator V5. Must sum to 1.
       * SCORE HIT is deliberately absent from this table - it is no
       * longer selectable under current rules - but its resolver/reward
       * config (`scoreHit` below), outcome type, counters, and historical
       * data all remain fully supported (see `outcomeWeightsLegacyV1`
       * below, and `src/core/chaosOutcome.ts`'s versioned resolver).
       * Rolled with a single draw from the injectable `chaosRandom` source
       * (separate from bite-placement RNG). The evaluation order used by
       * `src/core/chaosOutcome.ts`'s `resolveChaosOutcome()` for this pool
       * is JACKPOT / WORMHOLE / SHRINK / SPEED TRIAL, cumulative - see
       * that module's own doc for the exact half-open bands this produces.
       */
      outcomeWeightsCurrentV2: {
        jackpot: 0.3,
        wormhole: 0.25,
        shrink: 0.3,
        speedTrial: 0.15,
      },
      /**
       * FROZEN legacy CHAOS pool (pre-PR13-A) - Daily Generator V1-V4 use
       * this exactly forever (see `LevelDefinition.chaosRulesVersion`/
       * `src/daily/convert.ts`'s `chaosRulesVersionFor()`), so an
       * already-generated/stored legacy Daily challenge's CHAOS sequence
       * never silently reinterprets under `outcomeWeightsCurrentV2`'s
       * different weights. Must sum to 1. Evaluation order: JACKPOT /
       * WORMHOLE / SCORE HIT / SHRINK / SPEED TRIAL, cumulative - see
       * `src/core/chaosOutcome.ts`.
       */
      outcomeWeightsLegacyV1: {
        jackpot: 0.25,
        wormhole: 0.2,
        scoreHit: 0.2,
        shrink: 0.2,
        speedTrial: 0.15,
      },
      jackpot: {
        growth: 3,
        score: 60,
      },
      /**
       * WORMHOLE: a utility outcome - no immediate score or length. While
       * active, crossing an outer board edge wraps the snake to the
       * opposite edge; internal obstacles and self-collision stay lethal.
       * Lasts `durationMs` of active (pause-frozen) time - pausing simply
       * freezes the countdown, it never ends it early. Eating ANY other
       * fruit (SAFE/RUSH/CHAOS/STARFRUIT) does NOT end it - only its own
       * natural expiry, a fresh WORMHOLE roll (which refreshes the timer
       * back to `durationMs`, non-additive), or gate entry end it. See
       * `LevelGame.applyBiteEffects()`'s own doc and `wormhole.test.ts`
       * for the full lifecycle matrix.
       */
      wormhole: {
        durationMs: 10000,
      },
      /**
       * PR26 (SPEED TRIAL V2): `rewardGrowth`/`rewardScore` raised
       * 2/80 -> 3/100 - a successfully completed trial is now a more
       * meaningful skill reward. `rewardGrowth` is ordinary growth (feeds
       * Growth Pressure/the normal speed composition exactly like any
       * other length gain) - it deliberately never adds RUSH debt or any
       * SPEED-TRIAL-specific permanent speed penalty; see
       * `LevelGame.applyBiteEffects()`'s `"chaos"`/`speed-trial` case.
       */
      speedTrial: {
        durationMs: 6000,
        rewardGrowth: 3,
        rewardScore: 100,
      },
      scoreHit: {
        /** At or below this score, `flatLoss` is subtracted outright (can go negative). Above it, only `percentAbove` of the score survives (floored). Never clamped at 0 either way - CHAOS must always have an effect, even on a broke or already-negative score. */
        threshold: 100,
        flatLoss: 30,
        percentAbove: 0.3,
      },
      shrink: {
        segments: 2,
        scoreLoss: 20,
      },
    },
    spawn: {
      /** Always exactly one SAFE, one RUSH, one CHAOS. */
      count: 3,
      /** Delay before a new triplet appears after one is eaten. */
      respawnDelayMs: 250,
      /** Best-effort placement retries before falling back to any valid cells. */
      maxPlacementAttempts: 50,
      /** Best-effort cap on the spread between the three bites' distance from the head. */
      maxDistanceSpreadCells: 4,
    },
  },

  /**
   * The legendary fourth fruit - not a CHAOS outcome, a separate,
   * extremely rare bite that can appear alongside SAFE/RUSH/CHAOS. Its
   * eligibility is a single per-run roll (see `LevelGame`'s dedicated
   * `starfruitRandom` source), never re-rolled per fruit group.
   */
  starfruit: {
    /** Chance, rolled exactly once per run, that STARFRUIT is even eligible to appear this run. */
    probability: 0.02,
    /** Only the Nth normal SAFE/RUSH/CHAOS group of the run may reveal an eligible STARFRUIT alongside it - never the 1st. */
    revealGroupNumber: 2,
    /** How long the gate stays open after eating STARFRUIT before it closes again (unless the real target length is reached first). */
    gateDurationMs: 12000,
  },

  /** The snake's automatic, uninterruptible walk through an open gate. */
  gateExit: {
    /** Target total duration for the whole exit animation. */
    totalDurationMs: 1400,
    minStepMs: 40,
    maxStepMs: 160,
  },

  /**
   * Scoring V2: Skill Score (maneuvers + FLOW + speed) and the shared
   * anti-farming/rounding rules. Pace Bonus's own tunables live per-level
   * (see `LevelDefinition.pace`), not here, since a sane pace target is
   * inherently level-shaped (board size, target length) - everything that
   * IS constant across the whole campaign lives here instead, so tuning
   * Skill Score never means touching `src/core/LevelGame.ts` or any
   * rendering code.
   */
  scoring: {
    /** Base points per maneuver, before diminishing returns/FLOW/speed. LAST-SECOND TURN is NOT here (PR26 V3) - see `lastSecondTurn` below, which replaces a fixed base + the shared `diminishingReturns` table below with its own occurrence curve and its own speed multiplier, for this ONE maneuver type only. */
    maneuvers: {
      perfectFold: 20,
      tailGraze: 15,
      threadTheNeedle: 25,
      /** Flat bonus added once when >= 2 different maneuvers resolve on the same executed movement step. Weighted by the shared `maneuverSpeedWeight` below, never by LAST-SECOND TURN's own speed multiplier, even when LAST-SECOND TURN is one of the >= 2 credited maneuvers - it is a generic multi-maneuver combo bonus, not any one type's own reward component. */
      doubleDangerBonus: 10,
    },
    /** PERFECT FOLD requires at least this logical snake length. */
    perfectFold: { minLength: 6 },
    /**
     * Per-maneuver-type diminishing returns, by credited-occurrence index
     * (1st, 2nd, 3rd) across the whole run and across distinct anchors -
     * index 0 is the 1st occurrence's factor. Any occurrence beyond this
     * table's length (4th+) is worth 0, not looked up here. Applies to
     * PERFECT FOLD / TAIL GRAZE / THREAD THE NEEDLE - LAST-SECOND TURN has
     * its own, separate occurrence curve (`lastSecondTurn.occurrenceCurve`
     * below) since PR26 V3.
     */
    diminishingReturns: [1, 0.5, 0.25] as readonly number[],
    /**
     * PR26 LAST-SECOND TURN V3 (locked design - playtested V2's shared
     * base-value + `diminishingReturns` pairing as too weak: only 3
     * rewarded occurrences, a rapidly-diminishing curve, and not enough
     * upside for the maneuver at very high speed). Replaces BOTH of
     * LAST-SECOND TURN's old reward components; every OTHER maneuver type
     * keeps `maneuvers`/`diminishingReturns`/`maneuverSpeedWeight` above
     * completely unchanged.
     */
    lastSecondTurn: {
      /**
       * The EXACT base score for the Nth REWARDED occurrence this run
       * (index 0 = 1st occurrence), across the whole run and across
       * distinct anchors, exactly like `diminishingReturns` above - but
       * here every entry is the full base value itself, not a factor
       * applied to some other fixed base. The 16th+ occurrence may still
       * be detected/anchor-deduped internally (existing anti-farming
       * protections are unchanged) but is never looked up here - it scores
       * exactly 0, same as a 4th+ occurrence does for every other maneuver
       * type. Locked values - do not approximate.
       */
      occurrenceCurve: [20, 25, 35, 45, 50, 47, 43, 39, 35, 31, 27, 23, 19, 15, 10] as readonly number[],
      /**
       * A SEPARATE weighting from `maneuverSpeedWeight` above (which still
       * applies, unchanged, to every other maneuver type) - x1 at the
       * run/mode's own base speed, x4 at 75ms (the same absolute floor
       * SPEED TRIAL/the post-95 RUSH continuation use elsewhere), quadratic
       * in between. See `computeLastSecondTurnSpeedMultiplier()` in
       * `src/core/scoring/lastSecondTurnSpeedMultiplier.ts` for the exact
       * formula, and that module's own doc for why this MUST be fed the
       * persistent/underlying speed, never SPEED TRIAL's temporary
       * override.
       */
      speedMultiplier: { min: 1, max: 4 },
      /**
       * PR26 LAST-SECOND TURN V4/V5 (locked design - playtesting V3 found
       * players could deliberately manufacture repeated LAST-SECOND TURNs
       * back-to-back without ever really leaving danger). After a REWARDED
       * LAST-SECOND TURN, scoring for this maneuver type disarms until the
       * player completes this many CONSECUTIVE safe-forward movement ticks
       * (see `ManeuverTracker`'s own re-arm state machine) - a single
       * dangerous-forward tick resets progress back to 0, never partial
       * credit. Ticks, never elapsed time, so this behaves identically at
       * every movement speed.
       *
       * V5: raised 4 -> 7 (Campaign calibration audit found the V4 value
       * combined with FLOW's 6-move window let a player farm LAST-SECOND
       * TURN at near-zero risk while riding one continuous FLOW streak to
       * its x2 cap). 7 pushes the minimum possible gap between two
       * back-to-back LAST-SECOND credits to 8 moves, itself already past
       * FLOW's window - see `ManeuverTracker`'s own separate, EXPLICIT
       * LAST-SECOND-TURN-cannot-chain-FLOW-into-itself rule below for why
       * this isn't relied on alone.
       */
      reArmSafeMoves: 7,
    },
    flow: {
      /** A credited skill moment continues FLOW if it's within this many executed moves of the previous credited one. */
      windowMoves: 6,
      /** Multiplier by FLOW moment count (1st, 2nd, 3rd, ...) - index 0 is moment 1. The last entry is reused for every moment beyond it (4+). */
      multipliers: [1, 1.25, 1.5, 2] as readonly number[],
      maxMultiplier: 2,
    },
    /**
     * `maneuverSpeedWeight = clamp(baseMovementInterval / effectiveMovementInterval, min, max)`
     * - normal speed is exactly `min` (x1). Skill Score's own movement-speed
     * weighting, deliberately named apart from the gameplay Speed Engine's
     * composed boost factor (`src/core/SpeedEngine.ts`) so the two can
     * never be confused - see `src/core/scoring/maneuverSpeedWeight.ts`.
     */
    maneuverSpeedWeight: { min: 1, max: 1.5 },
    /**
     * TRICK BITE / CLUTCH BITE: payoff bonuses that connect a positively
     * credited maneuver to the fruit-choice loop - not a new geometric
     * maneuver, so neither is multiplied by FLOW/speed, subject to
     * diminishing returns, or counted toward DOUBLE DANGER. Flat integers,
     * added straight into Skill Score. See `TrickBiteTracker`.
     */
    biteBonus: {
      /** Fruit eaten within the next `windowMoves` executed movement steps after a credited maneuver (not the same step - that's CLUTCH BITE instead). */
      trickBite: 20,
      /** Fruit eaten on the exact same executed movement step a maneuver is credited on - replaces TRICK BITE for that fruit, never stacks with it. */
      clutchBite: 35,
      /** How many executed movement steps after a credited maneuver the TRICK BITE window stays open (steps 1, 2, 3 - a 4th-or-later step no longer qualifies). */
      windowMoves: 3,
      /**
       * PR13-B: how many executed movement steps after a successful WORMHOLE
       * boundary wrap the window stays open (shorter than a geometric
       * maneuver's `windowMoves` - WORMHOLE is a lesser opener, never
       * allowed to shorten a better active geometric window; see the
       * non-degrading overlap rule in `TrickBiteTracker`).
       */
      wormholeWindowMoves: 2,
    },
  },

  colors: {
    background: 0x0f1117,
    boardBackground: 0x171a24,
    gridLine: 0x232838,
    snakeHead: 0x7dffb3,
    snakeBody: 0x35c97a,
    food: 0xff5f6d,
    text: 0xf5f7fa,
    accent: 0x5b8cff,
    accentHover: 0x6f9bff,
    danger: 0xff5f6d,

    obstacle: 0x2a3145,
    obstacleBorder: 0x4a5578,

    gateLocked: 0x2a2230,
    gateLockedBorder: 0x6b5a72,
    gateOpen: 0x35c97a,
    gateOpenGlow: 0x7dffb3,

    /** SAFE is drawn as a red apple (not a plain green dot), so its shape reads as "safe/food" at a glance, independent of color. */
    biteSafe: 0xe0473e,
    biteSafeContour: 0x7a1f1f,
    biteSafeStem: 0x6b4a2b,
    biteSafeLeaf: 0x4fbf6b,
    biteSafeShine: 0xffd0cb,
    biteRush: 0xf5c34d,
    biteChaos: 0x9b6bff,
    star: 0xf5c34d,
    starEmpty: 0x3a3f52,

    /** SPEED TRIAL's own dedicated blue/cyan - deliberately distinct from RUSH's gold (`biteRush`) and WORMHOLE's cyan (`wormholeCyan`), so its badge/edge-streak/impact never reads as either of those effects. */
    speedTrialBlue: 0x4d9bff,

    /** WORMHOLE: cyan/violet portal energy - the edge glow while active, and the head's entry/exit wrap flash. */
    wormholeCyan: 0x4be8ff,
    wormholeViolet: 0xb26bff,

    /** STARFRUIT: a prismatic white/cyan body with a violet edge glow, clearly distinct from RUSH's flat gold. */
    starfruitBody: 0xf5fbff,
    starfruitGlow: 0xc9a9ff,
    starfruitSparkle: 0x7dfff0,

    /**
     * Portals V1 (Saturn World): persistent pair-marker colors, cycled by
     * pair index (pair 0 -> index 0, pair 1 -> index 1, ...). Deliberately
     * distinct from WORMHOLE's own cyan/violet (`wormholeCyan`/
     * `wormholeViolet`) and from every bite color - a permanent map
     * portal must never be visually confusable with the temporary CHAOS
     * boundary-wrap effect.
     */
    portalPairs: [0x50e3c2, 0xd6b56d] as readonly number[],

    /**
     * BLACK HOLE V1 (Saturn World): a near-black core with a hot-amber
     * accretion-ring glow - deliberately never violet/cyan (reserved for
     * WORMHOLE/CHAOS/portals) and never a flat gray (reserved for plain
     * obstacles), so it reads as its own, clearly hazardous hazard type
     * at a glance.
     */
    blackHoleCore: 0x0a0710,
    blackHoleRing: 0xff8c42,
  },
} as const;
